<template>
  <router-link 
    :to="disabled ? '#' : to" 
    class="category-card-link" 
    :class="{ 'disabled': disabled }" 
    @click="handleClick"
  >
    <div class="category-card">
      <div class="card-content">
        <div class="icon-wrapper">
          <span class="category-icon">{{ icon }}</span>
        </div>
        
        <div class="text-wrapper">
          <h3 class="category-title">{{ title }}</h3>
          <p class="category-description">{{ description }}</p>
        </div>
        
        <div v-if="badge" class="badge-wrapper">
          <span class="category-badge" :class="badgeColorClass">{{ badge }}</span>
        </div>
        
        <div v-if="!disabled" class="arrow-wrapper">
          <span class="arrow-icon">→</span>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import { message } from 'ant-design-vue'

export default {
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      default: ''
    },
    to: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: '📁'
    },
    badge: {
      type: String,
      default: ''
    },
    badgeColor: {
      type: String,
      default: 'blue'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isPremiumLocked: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    badgeColorClass() {
      const colors = {
        blue: 'badge-blue',
        orange: 'badge-orange',
        green: 'badge-green',
        red: 'badge-red',
        purple: 'badge-purple'
      }
      return colors[this.badgeColor] || 'badge-blue'
    }
  },
  methods: {
    handleClick(e) {
      if (this.disabled) {
        e.preventDefault()
        if (this.isPremiumLocked) {
          this.$emit('premium-click')
        } else {
          message.info(this.$t('home.sectionNotReady'))
        }
      }
    }
  }
}
</script>

<style scoped>
.category-card-link {
  text-decoration: none;
  display: block;
  transition: all 0.2s ease;
}

.category-card-link:hover {
  transform: translateY(-2px);
}

.category-card-link.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.category-card-link.disabled:hover {
  transform: none;
}

.category-card {
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 16px;
  transition: all 0.3s ease;
  height: 100%;
  cursor: pointer;
}

.category-card-link:not(.disabled) .category-card:hover {
  border-color: var(--primary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.card-content {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.15));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.category-icon {
  font-size: 24px;
  line-height: 1;
}

.text-wrapper {
  flex: 1;
  min-width: 0;
}

.category-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 4px;
  color: var(--text);
  line-height: 1.3;
  word-break: break-word;
}

.category-description {
  font-size: 12px;
  color: var(--text-soft);
  margin: 0;
  line-height: 1.4;
  word-break: break-word;
}

.badge-wrapper {
  flex-shrink: 0;
  margin-left: 8px;
}

.category-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 20px;
  white-space: nowrap;
  display: inline-block;
}

.badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
}

.badge-orange {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.badge-green {
  background: rgba(34, 197, 94, 0.12);
  color: #22c55e;
}

.badge-red {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.badge-purple {
  background: rgba(139, 92, 246, 0.12);
  color: #8b5cf6;
}

.arrow-wrapper {
  flex-shrink: 0;
  margin-left: 8px;
}

.arrow-icon {
  font-size: 18px;
  color: var(--text-soft);
  transition: transform 0.2s ease;
}

.category-card-link:not(.disabled) .category-card:hover .arrow-icon {
  transform: translateX(4px);
  color: var(--primary);
}

/* Tablet (768px - 1024px) */
@media (max-width: 1024px) {
  .category-card {
    padding: 14px;
  }
  
  .icon-wrapper {
    width: 44px;
    height: 44px;
  }
  
  .category-icon {
    font-size: 22px;
  }
  
  .category-title {
    font-size: 15px;
  }
  
  .category-description {
    font-size: 11px;
  }
}

/* Mobile (576px - 768px) */
@media (max-width: 768px) {
  .category-card {
    padding: 12px;
  }
  
  .card-content {
    gap: 12px;
  }
  
  .icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }
  
  .category-icon {
    font-size: 20px;
  }
  
  .category-title {
    font-size: 14px;
    margin-bottom: 2px;
  }
  
  .category-description {
    font-size: 10px;
  }
  
  .badge-wrapper {
    margin-left: 4px;
  }
  
  .category-badge {
    font-size: 9px;
    padding: 3px 6px;
  }
  
  .arrow-icon {
    font-size: 16px;
  }
}

/* Small Mobile (320px - 576px) */
@media (max-width: 576px) {
  .category-card {
    padding: 12px;
  }
  
  .card-content {
    gap: 12px;
    flex-wrap: wrap;
  }
  
  .icon-wrapper {
    width: 44px;
    height: 44px;
  }
  
  .text-wrapper {
    flex: 1;
    min-width: calc(100% - 56px - 30px);
  }
  
  .badge-wrapper {
    position: absolute;
    top: 0;
    right: 0;
  }
  
  .category-badge {
    font-size: 9px;
    padding: 2px 6px;
  }
  
  .arrow-wrapper {
    position: absolute;
    bottom: 0;
    right: 0;
  }
  
  .arrow-icon {
    font-size: 14px;
  }
  
  .category-title {
    font-size: 13px;
    white-space: normal;
    overflow: visible;
  }
  
  .category-description {
    font-size: 10px;
    white-space: normal;
    overflow: visible;
  }
}

/* Extra small (320px dan kichik) */
@media (max-width: 380px) {
  .card-content {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
  
  .icon-wrapper {
    width: 48px;
    height: 48px;
  }
  
  .text-wrapper {
    text-align: center;
  }
  
  .badge-wrapper {
    position: static;
    margin-top: 4px;
  }
  
  .arrow-wrapper {
    position: static;
    margin-top: 8px;
  }
}
</style>